import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import CookieManager from '../../../CookieManager';

const ReconcilePage = ({ caption, users, roles }) => {
  /*
    NOTE:
    prendiamo supplier | invoice | mode dalla query string
    https://localhost:44479/reconcile?supCode=251511088&docYear=2025&docNumExt=26VN0056128&includeClosedDdt=true
  */

  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const qsSupCode = params.get('supCode');
  const qsDocYear = params.get('docYear');
  const qsDocNumExt = params.get('docNumExt');
  const qsIncludeClosedDdt = (() => {
    const raw = (params.get('includeClosedDdt') || '').toLowerCase();
    return raw === 'true' || raw === '1';
  })();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [orphanList, setOrphanList] = useState([]);
  const [selectedOrphanKeys, setSelectedOrphanKeys] = useState([]);
  const [isReconciling, setIsReconciling] = useState(false);
  const [searchedInvoiceArticle, setSearchedInvoiceArticle] = useState(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [forceSend, setForceSend] = useState(false);      //  nell'eventualità che manchi qualche ddt, ma non si vuol perdere il lavoro
  const [sendLoading, setSendLoading] = useState(false);  //  blocca il bottone mentre invio
  const [sendError, setSendError] = useState(null);       //  mostra un alert rosso con il motivo
  const [sendOk, setSendOk] = useState(null);             //  mostra un alert verde con esito

  useEffect(() => {
    if (!qsSupCode || !qsDocYear || !qsDocNumExt) {
      console.error("Parametri mancanti");
      return
    }

    if (!(CookieManager.isInUsers(users) || CookieManager.isInRoles(roles))) {
      console.error("Utente non autorizzato");
      return
    }

    const doFetch = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const payload = {
          supCode: Number(qsSupCode),
          docYear: Number(qsDocYear),
          docNumExt: qsDocNumExt,
          includeClosedDdt: qsIncludeClosedDdt
        };

        const res = await fetch('/stats/reconcile-retrieve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          setLoadError(`${res.status} - ${res.statusText}`);
          setInvoice(null);
          setOrphanList([]);
          return;
        }
        const data = await res.json();
        setInvoice({
          header: data.header,
          rows: (data.rows || []).map(r => ({
            ...r,
            ddtList: (r.ddtList || []).map(d => ({
              ...d,
              ddtKey: generateDdtKey(d)
            }))
          }))
        });
        setOrphanList((data.orphanList || []).map(o => ({
          ...o,
          ddtKey: generateDdtKey(o),
          isAssigned: false
        })));
      }
      catch (e) {
        setLoadError("Errore di comunicazione con il server");
        setInvoice(null);
        setOrphanList([]);
      }
      finally {
        setLoading(false);
      }
    };

    doFetch();

  }, [qsSupCode, qsDocYear, qsDocNumExt, qsIncludeClosedDdt, users, roles]
  );


  /*
    DEBUG: Si attiva ogni volta che invoice cambia davvero

    console.log("Fattura:", new Date().toLocaleString(), invoice);
    useEffect(() => {
      console.log("Fattura aggiornata:", new Date().toLocaleString(), invoice);
    }, [invoice]);
  
    NOTE:
    > .map per cambiare
    > .filter per eliminare
    > .flatMap per "appiattire" tutte le ddtList in un unico array, poi filtra solo i selezionati e sommiamo le qty

    const ddtTotQty = (invoice?.rows || [])
      .flatMap(r => r.ddtList || [])
      .filter(ddt => ddt.isSelected)
      .reduce((sum, ddt) => sum + (ddt.erpArtQty || 0), 0);
  */
  const invoiceTotQty = (invoice?.rows || [])
    .filter(r => !r.isRemoved)
    .reduce((sum, r) => sum + (r.artQty || 0), 0);


  const ddtTotQty = !isReconciling
    ? 0                                          // se !isReconciling non facciamo vedere
    : (invoice?.rows || [])                      // altrimenti filtriamo:
      .filter(r => !r.isRemoved)                 // righe attive
      .flatMap(r => r.ddtList || [])
      .filter(ddt => ddt.isSelected)             // selezionate
      .reduce((sum, ddt) => sum + Number(ddt.erpArtQty || 0), 0);

  const getReconcileStatus = () => {
    if (forceSend && ddtTotQty !== invoiceTotQty && ddtTotQty > 0) {
      return {
        alertType: 'danger',
        message: `Attenzione: Forza invio NTS attivo, anche se ddt (${ddtTotQty}) incoerenti con fattura (${invoiceTotQty})`,
        canSend: true
      };
    }

    if (invoiceTotQty === 0) {
      return {
        alertType: 'secondary',
        message: 'Attenzione: nessuna riga trovata da riconciliare',
        canSend: false
      };
    }

    else if (ddtTotQty === 0) {
      return {
        alertType: 'danger',
        message: 'Nessun DDT selezionato',
        canSend: false
      };
    }

    else if (ddtTotQty === invoiceTotQty) {
      return {
        alertType: 'success',
        message: 'Perfetto, DDT selezionati coerenti con la fattura!',
        canSend: true
      };
    }

    else if (ddtTotQty < invoiceTotQty) {
      return {
        alertType: 'warning',
        message: `Attenzione: mancanza di ${invoiceTotQty - ddtTotQty} pz da AGGIUNGERE.`,
        canSend: false
      };
    }

    else return {
      alertType: 'danger',
      message: `Attenzione: eccedenza di ${ddtTotQty - invoiceTotQty} pz da RIMUOVERE.`,
      canSend: false
    };

  };

  /*
    NOTE:
      stato derivato
    TODO: 
      valutare const reconcileStatus = useMemo(() => getReconcileStatus(), [invoiceTotQty, ddtTotQty]);
  */

  const reconcileStatus = getReconcileStatus();
  //const canSendToNts = (reconcileStatus.canSend || forceSend) && !sendLoading && !sendOk;
  const canSendToNts = reconcileStatus.canSend && !sendLoading && !sendOk;


  const calcInvoiceRowQty = (row) => {
    const invoiceRowQty = (row.ddtList || [])
      .filter(ddt => ddt.isSelected === true)
      .reduce((sum, ddt) => sum + Number(ddt.erpArtQty || 0), 0);

    return invoiceRowQty
  }

  //const calcInvoiceRowQty = (row) => {
  //  const list = row.ddtList || []

  //  const selected = list.filter(d => d.isSelected === true)

  //  let total = 0
  //  for (const d of selected) {
  //    const qty = Number(d.erpArtQty || 0)
  //    total += qty
  //  }
  //  return total
  //}

  const getRowQtyWarning = (row, coveredQty) => {
    const expected = Number(row.artQty || 0)
    const diff = coveredQty - expected

    if (diff === 0) return null
    if (diff < 0) return { type: 'text-danger', text: `Mancano ${Math.abs(diff)}` }
    return { type: 'text-warning', text: `Rimuovere ${diff}` }
  }

  /*
    NOTE:

    > qui sotto richiamiamo la funzione setDocumento, che è il setter di stato fornito dal hook useState
      MA invece di passare direttamente il nuovo stato con setDocumento(nuovoStato)
      viene usata la forma funzionale del setter setDocumento(prev => ...)

    > poi ricreiamo l'array righeFattura:
      per ogni riga con r.id trovato usa l'operatore spread ...r per copiare la riga impostando isRemoved a true
      altrimenti restituisce r senza modificarla
  */

  const handleRemoveAllInvoiceRows = () => {
    setInvoice(prev => ({
      ...prev,
      rows: (prev?.rows || []).map(r => ({
        ...r,
        isRemoved: true,
        ddtList: (r.ddtList || []).map(d => ({
          ...d,
          _prevSelected: !!d.isSelected,
          isSelected: false
        }))
      }))
    }))
  }

  const handleToggleInvoiceRow = (rowKey) => {
    setInvoice(prev => ({
      ...prev,
      rows: prev.rows.map(r => {
        if (r.rowKey !== rowKey) return r;

        const nextIsRemoved = !r.isRemoved;

        // 1) Se sto RIMUOVENDO: salvo selezione e poi spengo
        if (nextIsRemoved) {
          return {
            ...r,
            isRemoved: true,
            ddtList: (r.ddtList || []).map(d => ({
              ...d,
              _prevSelected: !!d.isSelected, // memoria
              isSelected: false              // spento per totali/payload
            }))
          };
        }

        // 2) Se sto RIATTIVANDO: ripristino e pulisco la memoria
        return {
          ...r,
          isRemoved: false,
          ddtList: (r.ddtList || []).map(d => ({
            ...d,
            isSelected: d._prevSelected ?? !!d.isSelected,
            _prevSelected: undefined
          }))
        };
      })
    }));
  };


  //const handleToggleDdtRow = (invRowKey, ddtRowKeys) => {
  //  setInvoice(prev => ({
  //    ...prev,
  //    rows: prev.rows.map(r => {
  //      if (r.rowKey !== invRowKey) {
  //        return r;
  //      }

  //      return {
  //        ...r,
  //        ddtList: (r.ddtList || []).map(ddt => {

  //          const sameKeys =  // qui confrontiamo le ddt keys 
  //            ddt.docType === ddtRowKeys.docType &&
  //            ddt.docYear === ddtRowKeys.docYear &&
  //            ddt.docSerie === ddtRowKeys.docSerie &&
  //            ddt.docNum === ddtRowKeys.docNum;

  //          return sameKeys ? { ...ddt, isSelected: !ddt.isSelected } : ddt;

  //        })
  //      };
  //    })
  //  }));
  //};

  const handleToggleDdtRow = (invRowKey, ddtKey) => {
    setInvoice(prev => ({
      ...prev,
      rows: prev.rows.map(r =>
        r.rowKey !== invRowKey
          ? r
          : {
            ...r,
            ddtList: (r.ddtList || []).map(d =>
              d.ddtKey === ddtKey ? { ...d, isSelected: !d.isSelected } : d
            )
          }
      )
    }));
  };

  /*
    NOTE: RETRIEVE dei DDT
    NOTE:
    > questa struttura chiamata Mappa in Js e le chiavi sono stringhe o Dizionario
    > questo per ottimizzare efficienza dell'accesso ai dati: 
      Iterazione righeFattura > Associazione DDT istantanea
  
      Fosse stato un array, per ogni riga dell'array avremmo dovuto ciclare righeFattura:
      const righeDDT = ddtArray.filter(ddt => ddt.articleCode === rigaFattura.articleCode);
  
      Mentre così:
      const righeDDT = fakeDDTByArticle[rigaFattura.articleCode];
  
      const staticDdtList = {
        "TYRE-205-55-R16": [
          { id: 101, ddtNumber: "DDT001", qty: 2, isClosed: false },
          { id: 102, ddtNumber: "DDT002", qty: 4, isClosed: false },
          { id: 103, ddtNumber: "DDT003", qty: 8, isClosed: true }
        ],
        "TYRE-225-45-R17": [
          { id: 201, ddtNumber: "DDT010", qty: 1, isClosed: false }
        ],
        "TYRE-215-55-R16": [
          { id: 215, ddtNumber: "DDT011", qty: 4, isClosed: false },
          { id: 216, ddtNumber: "DDT012", qty: 2, isClosed: false },
          { id: 217, ddtNumber: "DDT013", qty: 6, isClosed: false }
        ],
        "TYRE-185-60-R15": [
          { id: 220, ddtNumber: "DDT016", qty: 5, isClosed: false }
        ]
      };
  
  */

  const startReconciliation = () => {
    /*
      NOTE: ora gli orfani arrivano direttamente dal DB, quindi rimuovo questa parte
  
        const allDdtList = Object.entries(staticDdtList).flatMap(([key, ddtArray]) =>
          ddtArray.map(ddt => ({
            ...ddt,
            articleCode: key
          }))
        );
  
        const updatedInvoiceRows = invoice.rows.map(r => {
          if (r.isRemoved) return { ...r, ddtList: [] };
  
          const linkedDdtList = (staticDdtList[r.articleCode] || [])
            .filter(ddt => (qsIncludeClosedDdt ? true : !ddt.isClosed))
            .map(ddt => ({ ...ddt, isSelected: !ddt.isClosed }));
  
          return { ...r, ddtList: linkedDdtList };
        });
  
        const assignedDdtIds = updatedInvoiceRows.flatMap(r => r.ddtList.map(d => d.id));
  
        const orphanList = allDdtList
          .filter(d => !assignedDdtIds.includes(d.id))
          .map(d => ({ ...d, isAssigned: false }));
  
        setInvoice(prev => ({ ...prev, rows: updatedInvoiceRows }));
        setOrphanList(orphanList);
    */
    setSendError(null)
    setSendOk(null)
    setSendLoading(false)
    setIsReconciling(true);
  };

  /*
    NOTE:
    quando premo bottone aggiungi (ddt) deve:
    - taggare orfano come assegnato
    - modificare la riga di fattura, aggiungendo quel ddt
  
    const toggleOrphanRow = (selectedDdt) => {
      if (!searchedInvoiceArticle) return;
  
      const targetInvoiceRowKey = searchedInvoiceArticle.rowKey;
  
      setInvoice(prev => ({
        ...prev,
        rows: prev.rows.map(r => {
          if (r.rowKey === targetInvoiceRowKey) {
            return {
              ...r,
              ddtList: [...(r.ddtList || []), { ...selectedDdt, isSelected: true }]
            };
          }
          return r;
        })
      }));
  
      setOrphanList(prevOrphans =>
        prevOrphans.map(o =>
          o.id === selectedDdt.id
            ? { ...o, isAssigned: true }
            : o
        )
      );
  
      setManualModalOpen(false);
      setSearchedInvoiceArticle(null);
    };

    const handleToggleTemporaryOrphan = (selectedDdt) => {
    const ddtKey = `${selectedDdt.docType}-${selectedDdt.docYear}-${selectedDdt.docSerie}-${selectedDdt.docNum}`;

    setSelectedOrphanKeys(prev =>
      prev.includes(ddtKey)              // KEY  c'è?
        ? prev.filter(k => k !== ddtKey) // SI > Uncheck 
        : [...prev, ddtKey]              // NO > Check (selezione multipla) 
    );
  };
  */

  const handleToggleTemporaryOrphan = (ddtKey) => {
    setSelectedOrphanKeys(prev =>
      prev.includes(ddtKey)              // KEY  c'è?
        ? prev.filter(k => k !== ddtKey) // SI > Uncheck 
        : [...prev, ddtKey]              // NO > Check (selezione multipla) 
    );
  };

  const handleConfirmOrphanSelection = () => {
    if (!searchedInvoiceArticle) return;

    const ddtToAdd = orphanList
      .filter(o => selectedOrphanKeys.includes(o.ddtKey))
      .map(o => ({ ...o, isSelected: true }));

    setInvoice(prev => ({
      ...prev,
      rows: prev.rows.map(r =>
        r.rowKey === searchedInvoiceArticle.rowKey
          ? { ...r, ddtList: [...(r.ddtList || []), ...ddtToAdd] }
          : r
      )
    }));


    setOrphanList(prev => prev.filter(o => !selectedOrphanKeys.includes(o.ddtKey)));
    //setOrphanList(prev => prev.filter(o => {
    //  const key = `${o.docType}-${o.docYear}-${o.docSerie}-${o.docNum}`;
    //  return !selectedOrphanKeys.includes(key);
    //}));

    setSelectedOrphanKeys([]);
    setManualModalOpen(false);
  };

  //const toggleOrphanRow = (selectedDdt) => {
  //  if (!searchedInvoiceArticle) return;

  //  const targetInvoiceRowKey = searchedInvoiceArticle.rowKey;

  //  setInvoice(prev => ({
  //    ...prev,
  //    rows: prev.rows.map(r => {
  //      if (r.rowKey !== targetInvoiceRowKey) return r;

  //      // (opzionale) evita doppioni per chiave DDT
  //      const exists = (r.ddtList || []).some(d =>
  //        d.docType === selectedDdt.docType &&
  //        d.docYear === selectedDdt.docYear &&
  //        d.docSerie === selectedDdt.docSerie &&
  //        d.docNum === selectedDdt.docNum
  //      );

  //      if (exists) return r;

  //      return {
  //        ...r,
  //        ddtList: [...(r.ddtList || []), { ...selectedDdt, isSelected: true }]
  //      };
  //    })
  //  }));

  //  setOrphanList(prev =>
  //    prev.map(o => {
  //      const same =
  //        o.docType === selectedDdt.docType &&
  //        o.docYear === selectedDdt.docYear &&
  //        o.docSerie === selectedDdt.docSerie &&
  //        o.docNum === selectedDdt.docNum;

  //      return same ? { ...o, isAssigned: true } : o;
  //    })
  //  );

  //  setManualModalOpen(false);
  //  setSearchedInvoiceArticle(null);
  //};

  /*
    NOTE: 
    fetch verso il controller C#:
   
    const handleSendToNts = async () => {
    if (!invoice) return;
   
    const payload = buildSendPayload(invoice);
    console.log('Payload SEND:', payload);
   
    try {
      const res = await fetch('/stats/reconcile-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
   
      if (!res.ok) {
        const text = await res.text();
        console.error('SEND KO:', res.status, res.statusText, text);
        alert(`Errore invio: ${res.status} - ${res.statusText}`);
        return;
      }
   
      // può essere json o semplice testo, gestiamo entrambi
      const contentType = res.headers.get('content-type') || '';
      const result = contentType.includes('application/json')
        ? await res.json()
        : await res.text();
   
      console.log('SEND OK:', result);
      alert('Invio completato.');
    } catch (e) {
      console.error('Eccezione SEND:', e);
      alert(`Errore di comunicazione col server: ${e.message}`);
    }
  };
  */

  const handleSendToNts = async () => {
    if (!invoice) return;

    setSendLoading(true);
    setSendError(null);
    setSendOk(null);

    const payload = buildSendPayload(invoice);

    console.log("DEBUG - Payload finale:", payload);

    try {
      const res = await fetch('/stats/reconcile-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const contentType = res.headers.get('content-type') || '';
      const body = contentType.includes('application/json')
        ? await res.json()
        : await res.text();

      if (!res.ok) {
        setSendError(typeof body === 'string' ? body : (body?.message || `${res.status} ${res.statusText}`));
        return;
      }
      setSendOk(body); // ci salviamo la response per mostrarla
    } catch (e) {
      setSendError('ERRORE dicomunicazione col server.');
    } finally {
      console.log('OK dati inviati a NTS');
      setSendLoading(false);
    }
  };

  /*
    NOTE: 
    gestire eventualmente con CSS
  */
  const cardBg = isReconciling ? '#f8f9fa' : '#fff'
  const cardShadow = isReconciling
    ? '0 1px 0 rgba(0,0,0,0.06), 0 6px 14px rgba(0,0,0,0.06)'
    : 'none'

  //const cardAccent = 'rgba(0,0,0,0.25)'
  const cardAccent = 'rgba(0,0,0)'

  const cardLeft = isReconciling
    ? `5px solid ${cardAccent}`
    : '5px solid transparent'

  const cardRowStyle = {
    backgroundColor: cardBg,
    borderLeft: cardLeft
  }

  const cardTopRowStyle = isReconciling ? {
    ...cardRowStyle,
    boxShadow: cardShadow
  } : {}

  let content = null;

  /*
    NOTE:
    Se si accede da querystring e mancano i parametri
  */
  if (!qsSupCode || !qsDocYear || !qsDocNumExt) {
    content = (
      <div className="container my-5">
        <div className="alert alert-warning">
          Parametri mancanti. Torna alla Home e seleziona fornitore e fattura.
        </div>
        <Link className="btn btn-secondary" to="/">Torna alla Home</Link>
      </div>
    );
  }
  else if (CookieManager.isInUsers(users) || CookieManager.isInRoles(roles)) {
    content = (
      <div className="container my-5">
        {/*GRIGLIA di gestione FATTURE | DDT*/}
        <div className="card p-3">
          <h5 className="card-title">
            {caption}
          </h5>

          {/*Riga di controllo*/}
          <div className="text-muted">
            Fornitore: <strong>{qsSupCode}</strong> - Fattura: <strong>{qsDocNumExt} ({qsDocYear})</strong> - Inclusi DDT:  <strong>{qsIncludeClosedDdt ? 'Tutti' : 'Solo aperti'}</strong>
            <br></br>
            <br></br>
          </div>

          {loading && <div className="alert alert-secondary">Caricamento dati...</div>}
          {loadError && <div className="alert alert-danger">{loadError}</div>}

          {!loading && !loadError && !invoice && (
            <div className="alert alert-warning">Nessun dato caricato.</div>
          )}

          {/*Testata con intestazione Fattura*/}
          {/*<> equivale a React Fragment*/}
          {invoice && (
            <>
              < div className="mb-3">
                <strong>Fornitore:</strong> {invoice.header.supCode} - {invoice.header.supName}<br />
                <strong>Fattura:</strong> {invoice.header.docNumExt}<br />
                <strong>Data:</strong> {invoice.header.docDate}<br />
                <strong>Importo:</strong> {invoice.header.docTotal} &euro;
              </div>

              {/*Testata con quantitativi riconciliazione*/}
              <div className="mb-3">
                <strong>Totale prodotti in fattura: </strong> {invoiceTotQty}<br />
                <strong>Totale prodotti nei DDT selezionati: </strong> {ddtTotQty}
              </div>

              {/*Testata con alert per quantitativi riconciliazione && !sendError*/}
              {isReconciling && !sendOk &&
                <div className={`alert alert-${reconcileStatus.alertType}`}>
                  {reconcileStatus.message}
                </div>
              }

              {/*Testata con alert Send Nts*/}
              {sendError && (
                <div className="alert alert-danger">
                  {sendError}
                </div>
              )}

              {sendOk && (
                <div className="alert alert-success">
                  Invio a NTS completato
                </div>
              )}

              {/*Bottone forceSend*/}
              {isReconciling && ddtTotQty !== invoiceTotQty &&
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="forceSend"
                    checked={forceSend}
                    onChange={e => setForceSend(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="forceSend">
                    Forza invio a NTS
                  </label>
                </div>
              }

              {/*Bottone Submit*/}
              {isReconciling &&
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleSendToNts}
                  disabled={!canSendToNts} //{!!sendOk || sendLoading || !canSendToNts}   //{!!sendOk || sendLoading || ddtTotQty !== invoiceTotQty} 
                >
                  {sendLoading
                    ? 'Invio in corso...'
                    : (!!sendOk ? 'Inviato' : 'Invia lista DDT a NTS')
                  }
                </button>
              }

              {/*Bottone Rimuovi tutte le righe*/}
              {!isReconciling && invoice && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleRemoveAllInvoiceRows}
                >
                  Rimuovi tutte le righe
                </button>
              )}

              {/*Bottone Riconciliazione*/}
              {!isReconciling && (
                <button
                  className="btn btn-primary"
                  onClick={startReconciliation}
                  disabled={isReconciling} // evitiamo doppi click
                >
                  Avvia riconciliazione
                </button>
              )}
              {/*Testate Fattura*/}
              <table className="table table-borderless align-middle mt-3">
                <thead>
                  <tr
                    className="border-bottom"
                    style={cardTopRowStyle}
                  >
                    <th></th>
                    <th>Quantità</th>
                    <th>Articolo</th>
                    <th>Descrizione</th>
                  </tr>
                </thead>
                {invoice.rows
                  /*
                    NOTE: 
                    righe FATTURA passano solo se non stiamo riconciliando
                    oppure se stiamo riconciliando ma NON sono state rimosse
                  */
                  .filter(r => !isReconciling || !r.isRemoved)
                  .map(row => {
                    /*
                      NOTE: 
                      così ritorniamo un element 
                        row => (element)
                      così possiamo scrivere logica JS libera
                        row => {
                          ...logica varia 
                          return (element)
                        }
                    */

                    const coveredQty = isReconciling ? calcInvoiceRowQty(row) : 0;
                    const warn = isReconciling ? getRowQtyWarning(row, coveredQty) : null;

                    return (
                      < tbody key={row.rowKey} >
                        {/* Righe FATTURA */}

                        <tr
                          className={row.isRemoved ? 'opacity-50' : ''}
                          style={cardTopRowStyle}

                        >
                          <td>
                            {!isReconciling && (
                              <button
                                className={`btn btn-sm ${row.isRemoved ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                onClick={() => handleToggleInvoiceRow(row.rowKey)}
                                disabled={isReconciling}
                              >
                                {row.isRemoved ? 'Riattiva' : 'Rimuovi'}
                              </button>
                            )}
                            {isReconciling && warn &&
                              <div
                                className={`text-nowrap small fw-bold ${warn.type}`}
                              >
                                {warn.text}
                              </div>
                            }
                          </td>
                          <td className={isReconciling ? "text-nowrap fw-bold py-3" : "text-nowrap"}>{row.artQty}</td>
                          <td className={isReconciling ? "text-nowrap fw-bold py-3" : "text-nowrap"}>{row.artCode}</td>
                          <td>{row.artDesc}</td>
                        </tr>

                        {/*Bottone MODALE */}
                        {isReconciling && (
                          <tr style={cardRowStyle}>
                            {warn && (
                              <td colSpan="4" className="p-0"> {/* p-0 rimuove lo spazio interno della cella */}
                                <button
                                  className="btn btn-outline-primary w-100 border-0"
                                  style={{ borderRadius: 0, padding: '10px 0', cursor: 'pointer' }}
                                  onClick={() => {
                                    setSearchedInvoiceArticle(row)
                                    setManualModalOpen(true)
                                  }}
                                >
                                  + Aggiungi DDT
                                </button>
                              </td>
                            )}
                          </tr>
                        )}
                        {/* Righe DDT associate */}
                        {isReconciling && row.ddtList?.map(ddt => (
                          <tr
                            key={`ddt-${row.rowKey}-${ddt.docType}-${ddt.docYear}-${ddt.docSerie}-${ddt.docNum}`}
                            className={`small ${ddt.isClosed ? 'opacity-50' : ''}`}
                            style={cardRowStyle}
                          >
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!ddt.isSelected}
                                disabled={ddt.isClosed}
                                onChange={() => handleToggleDdtRow(row.rowKey, ddt.ddtKey)}
                              />
                            </td>
                            <td>{ddt.erpArtQty}</td>
                            <td className="text-muted">{ddt.erpArtCode}</td>
                            <td className="text-muted">
                              DDT {`${ddt.docYear}-${ddt.docSerie}-${ddt.docNum}`}
                              {ddt.isClosed && ' ( chiuso )'}
                            </td>
                          </tr>
                        ))}

                        {/* Riga di SEPARAZIONE visiva*/}
                        {isReconciling && (
                          <tr style={{ pointerEvents: 'none' }}>
                            <td
                              colSpan="4"
                              style={{
                                height: '14px',
                                padding: 0,
                                border: 'none',
                                backgroundColor: 'transparent'
                              }}
                            />
                          </tr>
                        )}
                      </tbody> // Fine scheda
                    ); // Fine return
                  }) // Fine map
                }
                {/*</tbody>*/}
              </table>
            </>
          )}
        </div>

        {/*MODALE per selezione DDT ORFANI*/}
        {
          manualModalOpen && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header d-flex flex-column align-items-start bg-white border-bottom shadow-sm py-3">

                    {/* Prima riga: Titolo e tasto chiudi */}
                    <div className="d-flex justify-content-between w-100 mb-2">
                      <h5 className="modal-title">Associa DDT Orfani</h5>
                      <button className="btn-close" onClick={() => { setManualModalOpen(false); setSelectedOrphanKeys([]); }}></button>
                    </div>

                    {/* Seconda riga: Dati articolo (Sempre visibili e bloccati) */}
                    <div className="w-100 p-2 bg-light border rounded">
                      <strong>{searchedInvoiceArticle?.artQty}x</strong> {searchedInvoiceArticle?.artCode}: {searchedInvoiceArticle?.artDesc}
                    </div>

                  </div>

                  <div className="modal-body">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Seleziona</th>
                          <th>Quantit&agrave;</th>
                          <th>Articolo ERP</th>
                          <th>Descrizione</th>
                          <th>DDT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphanList.map(ddt => {
                          const isChecked = selectedOrphanKeys.includes(ddt.ddtKey);

                          return (
                            <tr key={ddt.ddtKey} className={isChecked ? 'table-primary' : ''}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isChecked}
                                  onChange={() => handleToggleTemporaryOrphan(ddt.ddtKey)}
                                />
                              </td>
                              <td>{ddt.erpArtQty}</td>
                              <td>{ddt.erpArtCode}</td>
                              <td>{ddt.erpArtDesc}</td>
                              <td>{`${ddt.docSerie}/${ddt.docNum}`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => { setManualModalOpen(false); setSelectedOrphanKeys([]); }}>
                      Annulla
                    </button>
                    <button className="btn btn-primary" onClick={handleConfirmOrphanSelection}>
                      Conferma e Aggiungi ({selectedOrphanKeys.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      </div >
    )
  }

  return content;

};


/*
    NOTE: 
    Creiamo un oggetto DIZIONARIO MAP con chiavi composte
    È una tecnica standard di DEDUPLICAZIONE dei dati basata su chiavi multiple

    A) Prima versione quando non avevo ddtKey
      const buildSendPayload = (reconciledInvoice) => {
        const docNumExt = reconciledInvoice?.header?.docNumExt || '';

        const selected = (reconciledInvoice?.rows || [])
          .filter(r => !r.isRemoved)
          .flatMap(r => r.ddtList || [])
          .filter(d => d.isSelected)
          .map(d => ({
            docType: d.docType,
            docYear: d.docYear,
            docSerie: d.docSerie,
            docNum: d.docNum
          }));

        // Per chiave composta
        const map = {};
        for (const d of selected) {
          const k = `${d.docType}|${d.docYear}|${d.docSerie}|${d.docNum}`;
          map[k] = d;
        }

        return {
          docNumExt,
          ddtList: Object.values(map)
        };
      };

    B) Seconda versione con ddtKey:
      const buildSendPayload = (reconciledInvoice) => {
        const docNumExt = reconciledInvoice?.header?.docNumExt || '';

        const selected = (reconciledInvoice?.rows || [])
          .filter(r => !r.isRemoved)
          .flatMap(r => r.ddtList || [])
          .filter(d => d.isSelected);

        const map = {};
        for (const d of selected) {
          //const k = `${d.docType}|${d.docYear}|${d.docSerie}|${d.docNum}`;
          //map[k] = d;
          map[d.ddtKey] = d;
        }

        return {
          docNumExt,
          ddtList: Object.values(map).map(d => ({
            docType: d.docType,
            docYear: d.docYear,
            docSerie: d.docSerie,
            docNum: d.docNum
          }))
        };
      };

    C) Terza versione più compatta:
  */

const buildSendPayload = (reconciledInvoice) => {
  const docNumExt = reconciledInvoice?.header?.docNumExt || '';

  const map = new Map();

  (reconciledInvoice?.rows || [])
    .filter(r => !r.isRemoved)
    .flatMap(r => r.ddtList || [])
    .filter(d => d.isSelected)
    .forEach(d => {
      map.set(d.ddtKey, d);
    });

  const result = {
    docNumExt,
    ddtList: Array.from(map.values()).map(d => ({
      docType: d.docType,
      docYear: d.docYear,
      docSerie: (d.docSerie ?? '').trim(),
      docNum: d.docNum
    }))
  };

  return result
};

  //const generateDdtKey = (d) => `${d.docType}|${d.docYear}|${d.docSerie}|${d.docNum}`;
  const generateDdtKey = (d) => {
    const docType = d?.docType ?? 'M';
    const docYear = d?.docYear ?? '';
    const docSerie = (d?.docSerie ?? '').trim();
    const docNum = d?.docNum ?? '';
    return `${docType}|${docYear}|${docSerie}|${docNum}`;
  };

  export default ReconcilePage;
